import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type PropsWithChildren, createContext } from 'react';
import { EVENTS } from '../../constants/general';
import { AnalyticsHelper, type DataLayerAdditions } from '../../helpers/analytics/analytics.helper';
import { type AllGTMEvents, type GTMEvent, type GTMEventWrapper, type GTMPageData } from '../../helpers/analytics/gtm/gtm-types';
import { hashObject } from '../../utils/object/object.utils';

type EventTracker = (data: GTMEventWrapper<AllGTMEvents>) => Promise<void>;

export type EventTrackOptions = {
	makeUnique: boolean;
};
/**
 * @deprecated - exported for tests. Should not be used outside fo the
 * AnalyticsContextProvider
 *
 * Provides state management for tracking analytics events.
 * Only tracks events after their associated page is tracked.
 */
export class AnalyticsContextState {
	private pageHasBeenTracked: boolean;
	private eventQueue: GTMEvent[];
	private sentEvents: Set<number>;

	constructor(private eventTracker = AnalyticsHelper.track, private pageTracker = AnalyticsHelper.pageView) {
		this.pageHasBeenTracked = false;
		this.eventQueue = [];
		this.sentEvents = new Set<number>();
	}

	trackEvent = (event: GTMEvent, options: EventTrackOptions = { makeUnique: false }) => {
		if (this.shouldTrackEvent(event, options)) {
			if (options.makeUnique) {
				this.markSent(event);
			}
			if (this.pageHasBeenTracked) {
				this.eventTracker(event).catch(logError);
			} else {
				this.eventQueue.push(event);
			}
		}
	};

	trackPageView = (pageData: GTMPageData, dataLayerAdditions?: DataLayerAdditions) => {
		this.pageTracker(pageData, dataLayerAdditions);
		// track any events that were fired before the page was tracked
		this.pageHasBeenTracked = true;
		this.eventQueue.forEach(this.eventTracker);
		this.eventQueue = [];

		document.dispatchEvent(new Event(EVENTS.COMPLETED_PAGE_TRACK));
	};

	// #region Helpers
	private shouldTrackEvent(event: GTMEvent, options: EventTrackOptions) {
		if (options.makeUnique) {
			return !this.hasBeenSent(event);
		}
		return true;
	}

	private hasBeenSent(event: GTMEvent) {
		return this.sentEvents.has(this.getEventHash(event));
	}

	private markSent(event: GTMEvent) {
		this.sentEvents.add(this.getEventHash(event));
	}

	private getEventHash(event: GTMEvent): number {
		return hashObject(event);
	}

	// #endregion
}

/**
 * DUMMY_STATE allows unsafe usage of the context. However, we initialize the
 * context at the RouteWithLayout level, so in practice the context will never
 * not be initialized.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const DUMMY_STATE: AnalyticsContextState = {
	trackPageView: () => {},
	trackEvent: () => {}
} as unknown as AnalyticsContextState;

/**
 * Context for tracking analytics events for a page.
 * uses an AnalytisContextState instance to ensure events fire in order
 */
export const AnalyticsContext = createContext<AnalyticsContextState>(DUMMY_STATE);

type AnalyticsContextProviderProps = {
	eventTracker?: EventTracker;
	pageTracker?: () => void;
};
/**
 * Wraps children with the AnalyticsContex Provider.
 * Allows consuming children to fire events and page events.
 * Ensures that events only fire after a page event.
 */
export const AnalyticsContextProvider: FunctionComponent<PropsWithChildren<AnalyticsContextProviderProps>> = ({
	eventTracker,
	pageTracker,
	children
}) => {
	const analyticState = new AnalyticsContextState(eventTracker, pageTracker);
	return <AnalyticsContext.Provider value={analyticState}>{children}</AnalyticsContext.Provider>;
};
